import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { apiService } from "lib/apiService";

export default function FinancialInsights({ selectedGlobalYear, selectedGlobalMonth, currency }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const history = useHistory();
  const [financialInsights, setFinancialInsights] = useState(null);
  useEffect(() => {
    // Check if user is not logged in (localStorage is empty)
    if (!localStorage.getItem("userData")) {
      // Redirect to the login page
      history.push("/login");
    }
  }, [history]);

  //get userid form loca storage
  const storedUserData = localStorage.getItem("userData");
  let userId = "";
  let groupId = "";
  if (storedUserData) {
    const userData = JSON.parse(storedUserData);
    userId = userData.userId;
    groupId = userData.groupId;
  }
  useEffect(() => {
    if (!groupId) {
      setError("No group ID available. Please sign in.");
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");
        const insights = await apiService.getFinancialInsights(groupId, selectedGlobalYear, selectedGlobalMonth);
        setFinancialInsights(insights);
      } catch (err) {
        setError("Failed to load financial insights. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [groupId, selectedGlobalYear, selectedGlobalMonth]);

  if (!financialInsights) {
    return <p style={{ color: "#6b7280" }}>No financial insights available.</p>;
  }

  const insightCardStyle = {
    padding: "24px",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: '5px 10px 20px #eee inset',
  };

  const renderCard = (title, value, description, color = "#000", targetId = null) => (
    <div
      style={{
        ...insightCardStyle,
        cursor: targetId ? "pointer" : "default"
      }}
      onClick={() => {
        if (targetId) {
          const element = document.getElementById(targetId);
          if (element) {
            const yOffset = -150; // 🔥 adjust as needed (negative = more down)
            const y =
              element.getBoundingClientRect().top + window.pageYOffset + yOffset;

            window.scrollTo({ top: y, behavior: "smooth" });
          }
        }
      }}
    >
      <h4 style={{ fontWeight: 600, color: color, marginBottom: "12px" }}>{title}</h4>
      <div style={{ fontSize: "1.5rem", fontWeight: 700, color: color, marginBottom: "8px" }}>
        {value}
      </div>
      <p style={{ fontSize: "0.875rem", color: "#475569" }}>{description}</p>
    </div>
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px', marginBottom: "50px" }}>
      {renderCard(
        "Highest Debit Month",
        financialInsights.highestDebitMonth.month,
        `Highest expenses of ${currency}${parseFloat(financialInsights.highestDebitMonth.amount).toFixed(2)}`,
        "#065f46",
        "yearly-credit-debit-chart"
      )}
      {renderCard(
        "Lowest Debit Month",
        financialInsights.lowestDebitMonth.month,
        `Lowest expenses of ${currency}${parseFloat(financialInsights.lowestDebitMonth.amount).toFixed(2)}`,
        "#0369a1",
        "yearly-credit-debit-chart"
      )}
      {renderCard(
        "Avg. Debit",
        `${currency}${parseFloat(financialInsights.avgDebit.amount).toFixed(2)}`,
        `Across ${financialInsights.avgDebit.transactionCount} debit transactions this month.`,
        "#dc2626",
        "Net-balance-chart"
      )}
      {renderCard(
        "Highest Credit Month",
        financialInsights.highestCreditMonth.month,
        `Highest credit of ${currency}${parseFloat(financialInsights.highestCreditMonth.amount).toFixed(2)}`,
        "#6d28d9",
        "yearly-credit-debit-chart"
      )}
      {renderCard(
        "Lowest Credit Month",
        financialInsights.lowestCreditMonth.month,
        `Lowest credit of ${currency}${parseFloat(financialInsights.lowestCreditMonth.amount).toFixed(2)}`,
        "#c2410c",
        "yearly-credit-debit-chart"
      )}
      {renderCard(
        "Avg. Credit",
        `${currency}${parseFloat(financialInsights.avgCredit.amount).toFixed(2)}`,
        `Across ${financialInsights.avgCredit.transactionCount} credit transactions this month.`,
        "#ef4444",
        "Net-balance-chart"
      )}
    {/* UPDATED WITH AMOUNT IN BRACKETS (NO DECIMALS) */}
{renderCard(
  "This Month’s Trend",
  financialInsights.thisMonthTrend.trend === "Downward" ? "📉 Downward" : "📈 Upward",
  `Spending is ${parseFloat(
    financialInsights.thisMonthTrend.percentageChange
  ).toFixed(2)}% (${currency}${parseFloat(
    financialInsights.thisMonthTrend.amount
  ).toFixed(0)}) ${financialInsights.thisMonthTrend.trend.toLowerCase()} than last month.`,
  "#65a30d",
  "daily-expense-chart"
)}

{/* UPDATED WITH AMOUNT IN BRACKETS (NO DECIMALS) */}
{renderCard(
  "Top Spending Category",
  financialInsights.topSpendingCategory.category,
  `Accounts for ${parseFloat(
    financialInsights.topSpendingCategory.percentage
  ).toFixed(2)}% (${currency}${parseFloat(
    financialInsights.topSpendingCategory.total
  ).toFixed(0)}) of your total expenses`,
  "#9a3412",
  "category-trend-chart"
)}

{/* UPDATED WITH AMOUNT IN BRACKETS (NO DECIMALS) */}
{renderCard(
  "Credit vs Debit",
  financialInsights.incomeVsExpense.ratio,
  `Credits is ${parseFloat(
    financialInsights.incomeVsExpense.percentageHigher
  ).toFixed(2)}% (${currency}${parseFloat(
    financialInsights.incomeVsExpense.amountDifference
  ).toFixed(0)}) higher than total debits.`,
  "#15803d",
  "yearly-credit-vs-debit-chart"
)}
    </div>
  );
}